/**
 * ---------------------------------------------------------------------------
 * VENNER — Tira de videos de la página de producto
 * ---------------------------------------------------------------------------
 *
 * Una sola miniatura está activa a la vez: crece, se reproduce y, pasado el
 * turno, cede el lugar a la siguiente. El turno dura lo que diga el ajuste,
 * o lo que dure el video si el ajuste es 0.
 *
 * Todo arranca sólo cuando la tira entra en pantalla, y se detiene al salir,
 * para no gastar datos en videos que nadie está viendo.
 */

{
  class VennerVideoStrip extends HTMLElement {
    #ultimoScrollManual = 0;
    #autoScrolling = false;
    #autoScrollTimer = 0;

    #abortController;
    #observer;
    #preloadObserver;
    #timer;
    #index = 0;
    #inView = false;

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.vvs__item'));
      this.trackEl = this.querySelector('.vvs__track');

      if (!this.items.length) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Un desplazamiento a mano tiene prioridad: el automático no debe pelearse con el visitante.
      this.trackEl?.addEventListener('scroll', () => {
        if (!this.#autoScrolling) this.#ultimoScrollManual = Date.now();
      }, { passive: true, signal });

      this.captionEl = this.querySelector('[data-vvs-caption]');

      this.items.forEach((item, i) => {
        item.addEventListener('click', (event) => {
          // Si la miniatura es un enlace, el clic sólo la activa la primera
          // vez; ya activa, deja pasar el clic al enlace.
          if (item.dataset.href && item.classList.contains('is-active')) return;
          event.preventDefault();
          this.activate(i, { fromUser: true });
        }, { signal });
      });

      this.querySelectorAll('[data-vvs-sound]').forEach((button) => {
        button.addEventListener('click', () => this.toggleSound(), { signal });
      });

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.activate(this.#index);
            } else {
              this.stop();
            }
          }
        },
        { threshold: 0.35 }
      );

      this.#observer.observe(this);

      /*
        Los videos se dibujan con preload="none" para no descargarlos todos de golpe en móvil. Este
        segundo observador se adelanta 600px a la sección y va pidiendo los primeros, de modo que al
        llegar ya estén listos en vez de empezar a cargar en ese momento.
      */
      this.#preloadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#mostrarPortadas();
            this.#precargarDesde(0);
            this.#preloadObserver.disconnect();
          }
        },
        { rootMargin: '600px 0px' }
      );

      this.#preloadObserver.observe(this);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
      this.#preloadObserver?.disconnect();
      clearTimeout(this.#timer);
    }

    /**
     * Las portadas son perezosas, y una imagen perezosa dentro de un carrusel horizontal no se
     * pide hasta que se desliza hasta ella: por eso las miniaturas de la tercera en adelante se
     * quedaban como cuadros grises hasta que el visitante las arrastraba. Pesan unos cuantos
     * kilobytes, así que en cuanto la sección se acerca se piden todas. Quitarle `lazy` al
     * atributo `loading` arranca la descarga.
     */
    #mostrarPortadas() {
      this.querySelectorAll('.vvs__poster img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    get turnDuration() {
      return parseFloat(this.getAttribute('duration')) || 0;
    }

    get playAll() {
      return this.hasAttribute('play-all');
    }

    get soundOn() {
      return this.hasAttribute('sound-on');
    }

    /**
     * Lleva el video activo al centro del carril conforme avanza el turno, como hacen las tiras de
     * videos de las apps: los que no caben quedan fuera de vista hasta que les toca.
     *
     * Se mueve `scrollLeft` del carril en vez de usar scrollIntoView, que arrastraría también la página.
     * Si el visitante acaba de desplazar a mano, se le cede el paso durante unos segundos.
     */
    #centrarActivo({ inmediato = false } = {}) {
      const track = this.trackEl;
      const item = this.items[this.#index];

      if (!track || !item || track.scrollWidth <= track.clientWidth) return;
      if (!inmediato && Date.now() - this.#ultimoScrollManual < 4000) return;

      const destino = item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
      const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#autoScrolling = true;
      track.scrollTo({ left: Math.max(0, destino), behavior: suave ? 'smooth' : 'auto' });
      clearTimeout(this.#autoScrollTimer);
      this.#autoScrollTimer = setTimeout(() => { this.#autoScrolling = false; }, 700);
    }

    get preloadAhead() {
      const valor = parseInt(this.getAttribute('preload-ahead'), 10);

      return Number.isFinite(valor) ? Math.min(3, Math.max(0, valor)) : 1;
    }

    /**
     * Pide los siguientes videos mientras corre el actual, para que el cambio de turno sea instantáneo.
     * Sin esto cada video empieza a descargarse justo cuando le toca y el visitante ve un par de
     * segundos en blanco.
     */
    #precargarDesde(index) {
      const cuantos = this.preloadAhead;

      for (let salto = 0; salto <= cuantos; salto += 1) {
        const item = this.items[(index + salto) % this.items.length];
        const video = item?.querySelector('video');

        if (!video || video.dataset.precargado === 'si') continue;

        video.dataset.precargado = 'si';
        video.preload = 'auto';

        // load() aborta la reproducción en curso: sólo se llama si el video sigue intacto.
        if (video.paused && video.readyState === 0 && video.currentTime === 0) {
          video.load();
        }
      }
    }

    get playbackRate() {
      const valor = parseFloat(this.getAttribute('playback-rate'));

      // Se acota al rango del ajuste para que un valor raro no deje el video inservible.
      return Number.isFinite(valor) ? Math.min(2, Math.max(0.5, valor)) : 1;
    }

    /**
     * Algunos navegadores reinician playbackRate al cargar los metadatos, así que se aplica también
     * cuando el video queda listo, no solo antes de reproducirlo.
     */
    #aplicarVelocidad(video) {
      const rate = this.playbackRate;

      video.playbackRate = rate;

      if (video.readyState === 0) {
        video.addEventListener('loadedmetadata', () => { video.playbackRate = rate; }, { once: true });
      }
    }

    activate(index, { fromUser = false } = {}) {
      clearTimeout(this.#timer);
      this.#index = (index + this.items.length) % this.items.length;

      this.items.forEach((item, i) => {
        const isActive = i === this.#index;
        const video = item.querySelector('video');

        item.classList.toggle('is-active', isActive);

        if (!video) return;

        if (isActive) {
          video.muted = !this.soundOn;
          video.currentTime = 0;
          this.#aplicarVelocidad(video);
          this.#reflowProgress(item);
          video.play().then(() => item.classList.add('is-playing')).catch(() => {
            /* el navegador bloqueó la reproducción: se queda la portada */
          });
        } else {
          video.muted = true;
          if (this.playAll) {
            this.#aplicarVelocidad(video);
            video.play().then(() => item.classList.add('is-playing')).catch(() => {});
          } else {
            video.pause();
            item.classList.remove('is-playing');
          }
        }
      });

      this.#updateCaption();
      this.#centrarActivo({ inmediato: fromUser });
      this.#precargarDesde(this.#index + 1);

      if (!this.#inView && !fromUser) return;

      const activeVideo = this.items[this.#index].querySelector('video');

      if (this.turnDuration > 0) {
        this.#timer = setTimeout(() => this.next(), this.turnDuration * 1000);
      } else if (activeVideo) {
        // Sin duración fija, el turno acaba cuando acaba el video.
        activeVideo.onended = () => this.next();
      }
    }

    next() {
      this.activate(this.#index + 1);
    }

    stop() {
      clearTimeout(this.#timer);
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (video) video.pause();
      });
    }

    toggleSound() {
      const turningOn = !this.soundOn;
      this.toggleAttribute('sound-on', turningOn);

      // Sólo la miniatura activa suena, y nunca dos tiras a la vez.
      if (turningOn) {
        document.querySelectorAll('venner-video-strip[sound-on]').forEach((strip) => {
          if (strip !== this) strip.toggleSound();
        });
      }

      const activeVideo = this.items[this.#index]?.querySelector('video');
      if (activeVideo) activeVideo.muted = !turningOn;
    }

    #updateCaption() {
      if (!this.captionEl) return;
      this.captionEl.textContent = this.items[this.#index].dataset.caption || '';
    }

    /* Reinicia la animación de la barra de avance en cada turno. */
    #reflowProgress(item) {
      const bar = item.querySelector('.vvs__progress > span');
      if (!bar) return;
      bar.style.animation = 'none';
      void bar.offsetWidth;
      bar.style.animation = '';
    }
  }

  if (!window.customElements.get('venner-video-strip')) {
    window.customElements.define('venner-video-strip', VennerVideoStrip);
  }
}
